import type { SeriesOptionsType, XAxisPlotBandsOptions } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { sprayWindowBandColors } from 'settings/colors'

// Matches the relevant fields of insectPhenology_<id> series.* (value-type-definitions
// TOptimalSprayWindows / TPhenologyInsect). Read structurally so we don't couple to those types.
export type TSprayWindow = {
  modelType?: string | null
  startDate?: string
  endDate?: string
  startCumulativeDegreeDays?: number | null
  endCumulativeDegreeDays?: number | null
}

export type TFlightPoint = {
  modelType?: string | null
  date?: string | null
  cumulativeDegreeDays?: number | null
}

const SEMIOS_BAND_COLOR = sprayWindowBandColors.semios
const REGIONAL_BAND_COLOR = sprayWindowBandColors.regional
const SEMIOS_BAND_ID_PREFIX = 'DASHBOARD_SPRAY_WINDOW_SEMIOS_'
const REGIONAL_BAND_ID_PREFIX = 'DASHBOARD_SPRAY_WINDOW_REGIONAL_'

// Resolve a cumulative degree day threshold to a timestamp using the modeled flight points
// (each carries date + cumulativeDegreeDays). Returns the date of the first point that reaches
// the threshold; null if the data can't cover it.
const ddToMs = (
  targetDd: number | null | undefined,
  sortedFlights: { ms: number; dd: number }[],
): number | null => {
  if (targetDd === null || targetDd === undefined) return null

  const hit = sortedFlights.find((f) => f.dd >= targetDd)

  return hit ? hit.ms : null
}

const resolveBandMs = (
  window: TSprayWindow,
  flightsForModel: { ms: number; dd: number }[],
  timezone: string,
): { from: number; to: number } | null => {
  const fromMs = window.startDate
    ? +moment.tz(window.startDate, timezone)
    : ddToMs(window.startCumulativeDegreeDays, flightsForModel)

  const toMs = window.endDate
    ? +moment.tz(window.endDate, timezone)
    : ddToMs(window.endCumulativeDegreeDays, flightsForModel)

  if (fromMs === null || toMs === null) return null

  return { from: fromMs, to: toMs }
}

const makeLegendSeries = (
  name: string,
  color: string,
  idPrefix: string,
  bands: XAxisPlotBandsOptions[],
): SeriesOptionsType => ({
  type: 'line',
  id: `legend-${idPrefix}`,
  name,
  color,
  // a filled swatch reads better than a thin line for a shaded-band legend entry
  marker: { enabled: true, symbol: 'square', radius: 6 },
  lineWidth: 0,
  data: [],
  events: {
    legendItemClick: function () {
      const xAxis = this.chart.xAxis?.[0]

      if (!xAxis) return

      const willHide = this.visible

      if (willHide) {
        bands.forEach((b) => b.id && xAxis.removePlotBand(b.id))
      } else {
        bands.forEach((b) => xAxis.addPlotBand(b))
      }
    },
  },
})

/**
 * Builds Semios (green) + Regional/WSU (orange) spray-window plot bands on the chart's time axis.
 * Prefers each window's startDate/endDate; falls back to mapping its cumulative degree day
 * thresholds to dates via the modeled flight points when the dates aren't populated.
 */
export const buildSprayWindowBands = ({
  optimalSprayWindows,
  modeledFlightPredictions,
  timezone,
  semiosBandLabel = translate.phrases.banyanApp('Semios Spray Window'),
}: {
  optimalSprayWindows: TSprayWindow[]
  modeledFlightPredictions: TFlightPoint[]
  timezone: string
  // Legend label for the green (semios) band. OBLR overrides this to "Management Window".
  semiosBandLabel?: string
}): { plotBands: XAxisPlotBandsOptions[]; legendSeries: SeriesOptionsType[] } => {
  if (optimalSprayWindows.length === 0) return { plotBands: [], legendSeries: [] }

  // Semios and WSU models accumulate degree days on different scales, so the DD→date fallback
  // must map a window's threshold using flight points of the SAME model. Group flights by model
  // (and keep an all-models list as a last resort if a window's model has no flights).
  const toSortedFlights = (flights: TFlightPoint[]) =>
    flights
      .filter((f) => f.date && f.cumulativeDegreeDays !== null && f.cumulativeDegreeDays !== undefined)
      .map((f) => ({ ms: +moment.tz(f.date as string, timezone), dd: f.cumulativeDegreeDays as number }))
      .sort((a, b) => a.dd - b.dd)

  const allFlights = toSortedFlights(modeledFlightPredictions)
  // Group raw flight points by model in a single pass, then convert each bucket once.
  const rawByModel = new Map<string, TFlightPoint[]>()

  modeledFlightPredictions.forEach((f) => {
    const model = f.modelType ?? ''
    const arr = rawByModel.get(model) ?? []

    arr.push(f)

    rawByModel.set(model, arr)
  })

  const flightsByModel = new Map<string, { ms: number; dd: number }[]>()

  rawByModel.forEach((flights, model) => flightsByModel.set(model, toSortedFlights(flights)))

  const semiosBands: XAxisPlotBandsOptions[] = []
  const regionalBands: XAxisPlotBandsOptions[] = []

  optimalSprayWindows.forEach((window, i) => {
    const flightsForModel = flightsByModel.get(window.modelType ?? '') ?? allFlights
    const resolved = resolveBandMs(window, flightsForModel, timezone)

    if (!resolved) return

    const isSemios = window.modelType === 'semios'

    const band: XAxisPlotBandsOptions = {
      id: `${isSemios ? SEMIOS_BAND_ID_PREFIX : REGIONAL_BAND_ID_PREFIX}${i}`,
      from: resolved.from,
      to: resolved.to,
      color: isSemios ? SEMIOS_BAND_COLOR : REGIONAL_BAND_COLOR,
      zIndex: 1,
    }

    if (isSemios) {
      semiosBands.push(band)
    } else {
      regionalBands.push(band)
    }
  })

  const legendSeries: SeriesOptionsType[] = []

  if (semiosBands.length > 0) {
    legendSeries.push(
      makeLegendSeries(semiosBandLabel, SEMIOS_BAND_COLOR, SEMIOS_BAND_ID_PREFIX, semiosBands),
    )
  }

  if (regionalBands.length > 0) {
    legendSeries.push(
      makeLegendSeries(
        translate.phrases.banyanApp('Regional Spray Window'),
        REGIONAL_BAND_COLOR,
        REGIONAL_BAND_ID_PREFIX,
        regionalBands,
      ),
    )
  }

  return { plotBands: [...semiosBands, ...regionalBands], legendSeries }
}
